import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Loader2, Star, StarOff, Image as ImageIcon, Video, Smartphone, Monitor, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { SiteImageUpload } from './SiteImageUpload';
import { SiteVideoUpload } from './SiteVideoUpload';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  getHeroImageUrl,
  setHeroImageUrl,
  getHeroImages,
  addHeroImage,
  removeHeroImage,
  getHeroVideoConfig,
  setHeroMediaType,
  setHeroVideoUrlMp4,
  setHeroVideoUrlWebm,
  setHeroVideoPoster,
  setHeroMobileBehavior,
  HeroVideoConfig,
  HeroMediaType,
  HeroMobileBehavior,
} from '@/lib/admin/site-images';

export function HeroMediaManager() {
  // Image state (existing functionality)
  const [primaryUrl, setPrimaryUrl] = useState<string | null>(null);
  const [allImages, setAllImages] = useState<string[]>([]);
  
  // Video state (new functionality)
  const [videoConfig, setVideoConfig] = useState<HeroVideoConfig | null>(null);
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'image' | 'video'>('image');
  const { toast } = useToast();

  useEffect(() => {
    loadHeroMedia();
  }, []);

  const loadHeroMedia = async () => {
    try {
      setLoading(true);
      const [primary, images, videoConf] = await Promise.all([
        getHeroImageUrl(),
        getHeroImages(),
        getHeroVideoConfig(),
      ]);
      setPrimaryUrl(primary);
      setAllImages(images);
      setVideoConfig(videoConf);
      
      // Set active tab based on current media type
      setActiveTab(videoConf.mediaType === 'video' ? 'video' : 'image');
    } catch (error) {
      console.error('Error loading hero media:', error);
      toast({
        title: 'Error',
        description: 'Failed to load hero media settings',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // Image Handlers (existing functionality)
  // ============================================

  const handleImageUploaded = async (url: string) => {
    try {
      setSaving(true);
      
      // Add to images list if not already present
      if (!allImages.includes(url)) {
        await addHeroImage(url);
        setAllImages([...allImages, url]);
      }

      // Set as primary if no primary is set
      if (!primaryUrl) {
        await setHeroImageUrl(url);
        setPrimaryUrl(url);
      }

      toast({
        title: 'Success',
        description: 'Hero image uploaded successfully',
      });
    } catch (error) {
      console.error('Error handling image upload:', error);
      toast({
        title: 'Error',
        description: 'Failed to save hero image',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSetPrimary = async (url: string) => {
    try {
      setSaving(true);
      const success = await setHeroImageUrl(url);
      if (success) {
        setPrimaryUrl(url);
        toast({
          title: 'Success',
          description: 'Primary hero image updated',
        });
      } else {
        throw new Error('Failed to update primary image');
      }
    } catch (error) {
      console.error('Error setting primary image:', error);
      toast({
        title: 'Error',
        description: 'Failed to set primary hero image',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveImage = async (url: string) => {
    try {
      setSaving(true);
      const success = await removeHeroImage(url);
      if (success) {
        const updatedImages = allImages.filter(img => img !== url);
        setAllImages(updatedImages);

        // If removing primary, clear it
        if (primaryUrl === url) {
          await setHeroImageUrl('');
          setPrimaryUrl(null);
        }

        toast({
          title: 'Success',
          description: 'Hero image removed',
        });
      } else {
        throw new Error('Failed to remove image');
      }
    } catch (error) {
      console.error('Error removing image:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove hero image',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  // ============================================
  // Video Handlers (new functionality)
  // ============================================

  const handleMediaTypeChange = async (mediaType: HeroMediaType) => {
    try {
      setSaving(true);
      const success = await setHeroMediaType(mediaType);
      if (success) {
        setVideoConfig(prev => prev ? { ...prev, mediaType } : null);
        setActiveTab(mediaType);
        toast({
          title: 'Success',
          description: `Hero media type set to ${mediaType}`,
        });
      } else {
        throw new Error('Failed to update media type');
      }
    } catch (error) {
      console.error('Error setting media type:', error);
      toast({
        title: 'Error',
        description: 'Failed to update media type',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleVideoMp4Updated = async (url: string) => {
    try {
      setSaving(true);
      const success = await setHeroVideoUrlMp4(url);
      if (success) {
        setVideoConfig(prev => prev ? { ...prev, videoUrlMp4: url || null } : null);
        toast({
          title: 'Success',
          description: url ? 'MP4 video uploaded successfully' : 'MP4 video removed',
        });
      } else {
        throw new Error('Failed to update MP4 video');
      }
    } catch (error) {
      console.error('Error updating MP4 video:', error);
      toast({
        title: 'Error',
        description: 'Failed to update MP4 video',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleVideoWebmUpdated = async (url: string) => {
    try {
      setSaving(true);
      const success = await setHeroVideoUrlWebm(url);
      if (success) {
        setVideoConfig(prev => prev ? { ...prev, videoUrlWebm: url || null } : null);
        toast({
          title: 'Success',
          description: url ? 'WebM video uploaded successfully' : 'WebM video removed',
        });
      } else {
        throw new Error('Failed to update WebM video');
      }
    } catch (error) {
      console.error('Error updating WebM video:', error);
      toast({
        title: 'Error',
        description: 'Failed to update WebM video',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePosterImageUpdated = async (url: string) => {
    try {
      setSaving(true);
      const success = await setHeroVideoPoster(url);
      if (success) {
        setVideoConfig(prev => prev ? { ...prev, posterUrl: url || null } : null);
        toast({
          title: 'Success',
          description: url ? 'Poster image uploaded successfully' : 'Poster image removed',
        });
      } else {
        throw new Error('Failed to update poster image');
      }
    } catch (error) {
      console.error('Error updating poster image:', error);
      toast({
        title: 'Error',
        description: 'Failed to update poster image',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleMobileBehaviorChange = async (behavior: HeroMobileBehavior) => {
    try {
      setSaving(true);
      const success = await setHeroMobileBehavior(behavior);
      if (success) {
        setVideoConfig(prev => prev ? { ...prev, mobileBehavior: behavior } : null);
        toast({
          title: 'Success',
          description: `Mobile behavior set to ${behavior === 'static' ? 'show static image' : 'show video'}`,
        });
      } else {
        throw new Error('Failed to update mobile behavior');
      }
    } catch (error) {
      console.error('Error updating mobile behavior:', error);
      toast({
        title: 'Error',
        description: 'Failed to update mobile behavior',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const isVideoReady = videoConfig?.videoUrlMp4 || videoConfig?.videoUrlWebm;
  const hasPosterImage = !!videoConfig?.posterUrl;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Hero Media</CardTitle>
        <CardDescription>
          Manage the hero section background. Choose between static images or video backgrounds.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Media Type Selection */}
        <Tabs value={activeTab} onValueChange={(value) => handleMediaTypeChange(value as HeroMediaType)}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="image" className="flex items-center gap-2">
              <ImageIcon className="h-4 w-4" />
              Static Image
            </TabsTrigger>
            <TabsTrigger value="video" className="flex items-center gap-2">
              <Video className="h-4 w-4" />
              Video Background
            </TabsTrigger>
          </TabsList>

          {/* Image Mode Content */}
          <TabsContent value="image" className="space-y-6 mt-6">
            {/* Upload Section */}
            <SiteImageUpload
              imageType="hero"
              existingImageUrl={primaryUrl || undefined}
              onImageUpdated={handleImageUploaded}
              label="Upload Hero Image"
              description="Upload a new hero image. The first uploaded image will be set as primary. Images are optimized for up to 5K resolution."
              maxImages={1}
            />

            {/* Primary Image Display */}
            {primaryUrl && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Primary Hero Image</h4>
                <div className="relative group">
                  <img
                    src={primaryUrl}
                    alt="Primary hero"
                    className="w-full h-64 object-cover rounded-lg border border-border"
                  />
                  <div className="absolute top-2 left-2 bg-primary text-primary-foreground text-xs px-2 py-1 rounded flex items-center gap-1">
                    <Star className="h-3 w-3 fill-current" />
                    Primary
                  </div>
                </div>
              </div>
            )}

            {/* All Hero Images */}
            {allImages.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium">All Hero Images ({allImages.length})</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {allImages.map((url, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={url}
                        alt={`Hero image ${index + 1}`}
                        className="w-full h-48 object-cover rounded-lg border border-border"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors rounded-lg flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                        {url !== primaryUrl && (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => handleSetPrimary(url)}
                            disabled={saving}
                          >
                            <StarOff className="h-4 w-4 mr-2" />
                            Set Primary
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleRemoveImage(url)}
                          disabled={saving}
                        >
                          Remove
                        </Button>
                      </div>
                      {url === primaryUrl && (
                        <div className="absolute top-2 right-2 bg-primary text-primary-foreground text-xs px-2 py-1 rounded flex items-center gap-1">
                          <Star className="h-3 w-3 fill-current" />
                          Primary
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Video Mode Content */}
          <TabsContent value="video" className="space-y-6 mt-6">
            {/* Video requirements alert */}
            {!isVideoReady && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Upload at least one video file (MP4 required, WebM optional) and a poster image to enable video hero.
                </AlertDescription>
              </Alert>
            )}

            {/* MP4 Video Upload (Required) */}
            <div className="space-y-2">
              <SiteVideoUpload
                videoType="mp4"
                existingVideoUrl={videoConfig?.videoUrlMp4 || undefined}
                onVideoUpdated={handleVideoMp4Updated}
                label="MP4 Video (Required)"
                description="Primary video file. MP4 (H.264) has the widest browser support. Recommended: 1920x1080, 8-15 seconds, under 8MB."
              />
            </div>

            {/* WebM Video Upload (Optional) */}
            <div className="space-y-2">
              <SiteVideoUpload
                videoType="webm"
                existingVideoUrl={videoConfig?.videoUrlWebm || undefined}
                onVideoUpdated={handleVideoWebmUpdated}
                label="WebM Video (Optional)"
                description="Optional WebM video for better compression in Chrome/Firefox. 30-50% smaller file size than MP4."
              />
            </div>

            {/* Poster Image Upload (Required for video) */}
            <div className="space-y-2">
              <SiteImageUpload
                imageType="hero"
                existingImageUrl={videoConfig?.posterUrl || undefined}
                onImageUpdated={handlePosterImageUpdated}
                label="Poster Image (Required)"
                description="Static image shown while video loads and for users who prefer reduced motion. This is also shown on mobile if mobile behavior is set to static."
                maxImages={1}
              />
            </div>

            {/* Mobile Behavior Toggle */}
            <div className="space-y-4 p-4 border border-border rounded-lg">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-base flex items-center gap-2">
                    <Smartphone className="h-4 w-4" />
                    Mobile Video Behavior
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Choose how videos display on mobile devices
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <Button
                  variant={videoConfig?.mobileBehavior === 'static' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleMobileBehaviorChange('static')}
                  disabled={saving}
                  className="flex items-center gap-2"
                >
                  <ImageIcon className="h-4 w-4" />
                  Static Image
                </Button>
                <Button
                  variant={videoConfig?.mobileBehavior === 'video' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleMobileBehaviorChange('video')}
                  disabled={saving}
                  className="flex items-center gap-2"
                >
                  <Video className="h-4 w-4" />
                  Show Video
                </Button>
              </div>

              <p className="text-xs text-muted-foreground">
                {videoConfig?.mobileBehavior === 'static' 
                  ? 'Mobile users will see the poster image (recommended for performance)'
                  : 'Mobile users will see the video (uses more data and battery)'}
              </p>
            </div>

            {/* Video Preview */}
            {isVideoReady && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium flex items-center gap-2">
                  <Monitor className="h-4 w-4" />
                  Video Preview (Desktop)
                </h4>
                <div className="relative rounded-lg overflow-hidden border border-border bg-black">
                  <video
                    autoPlay
                    muted
                    loop
                    playsInline
                    poster={videoConfig?.posterUrl || undefined}
                    className="w-full h-64 object-cover"
                  >
                    {videoConfig?.videoUrlWebm && (
                      <source src={videoConfig.videoUrlWebm} type="video/webm" />
                    )}
                    {videoConfig?.videoUrlMp4 && (
                      <source src={videoConfig.videoUrlMp4} type="video/mp4" />
                    )}
                  </video>
                  <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                    <Video className="h-3 w-3" />
                    Live Preview
                  </div>
                </div>
              </div>
            )}

            {/* Accessibility note */}
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>Accessibility:</strong> Users with &quot;Reduce Motion&quot; enabled in their system settings will automatically see the poster image instead of the video.
              </AlertDescription>
            </Alert>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

// Re-export the old name for backwards compatibility
export { HeroMediaManager as HeroImageManager };
